import { useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

import clsx from 'clsx';

import useTranslation from '@/utils/hooks/translation/useTranslation';

import PropTypes from 'prop-types';

// Component
import Icon from '@/components/base/Icon';
import Loader from '@/components/base/Loader';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const MediaDocumentLoading = () => (
    <div className="flex h-[400px] w-full items-center justify-center rounded-md bg-icoGray-300 dark:bg-icoDarkMode-anthracite">
        <Loader className="h-8 w-8 text-icoBlue" />
    </div>
);

const MediaDocumentError = ({ t }) => (
    <div className="flex h-[400px] w-full items-center justify-center rounded-md bg-icoGray-300 dark:bg-icoDarkMode-anthracite">
        <div className="flex flex-col items-center space-y-3 text-sm text-icoGray-400 dark:text-icoDarkMode-wolf">
            <Icon icon="content" className="text-3xl" />
            <span>{t('scheduler_funnel.error_preview')}</span>
        </div>
    </div>
);

const MediaDocument = ({ pdf }) => {
    const { t } = useTranslation('scheduler');
    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);

    function onDocumentLoadSuccess({ numPages }) {
        setNumPages(numPages);
        setPageNumber(1);
    }

    function previousPage() {
        if (pageNumber <= 1) return;
        setPageNumber((prevPageNumber) => prevPageNumber - 1);
    }

    function nextPage() {
        if (pageNumber >= numPages) return;
        setPageNumber((prevPageNumber) => prevPageNumber + 1);
    }

    return (
        <>
            <Document
                file={pdf}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                onLoadSuccess={onDocumentLoadSuccess}
                loading={MediaDocumentLoading}
                noData={() => MediaDocumentError({ t })}
                error={() => MediaDocumentError({ t })}>
                <Page pageNumber={pageNumber} loading={MediaDocumentLoading} />
            </Document>
            {numPages ? (
                <div className="flex w-full items-center justify-center space-x-3 bg-transparent p-3">
                    <button
                        className={clsx(
                            'z-1 h-8 w-8 rounded-full border border-icoGray-300 bg-white dark:border-icoDarkMode-wolf dark:bg-icoDarkMode-deepBlack',
                            { 'disabled cursor-default opacity-40': pageNumber <= 1 }
                        )}
                        onClick={previousPage}>
                        <Icon icon="chevron-left" className="text-xxs text-icoGray-500" />
                    </button>
                    <p className="font-GMedium text-xs text-icoBlack">
                        {pageNumber || (numPages ? 1 : '--')} / {numPages || '--'}
                    </p>
                    <button
                        className={clsx(
                            'z-1 h-8 w-8 rounded-full border border-icoGray-300 bg-white dark:border-icoDarkMode-wolf dark:bg-icoDarkMode-deepBlack',
                            { 'disabled cursor-default opacity-40': pageNumber >= numPages }
                        )}
                        onClick={nextPage}>
                        <Icon icon="chevron-right" className="text-xxs text-icoGray-500" />
                    </button>
                </div>
            ) : null}
        </>
    );
};

MediaDocument.propTypes = {
    pdf: PropTypes.string.isRequired
};

export default MediaDocument;
